import { useEffect, useState, useCallback } from 'react';
import { FaTrash, FaEdit, FaExternalLinkAlt, FaCheck, FaTimes } from 'react-icons/fa';
import {
  EntityLink,
  LinkSource,
  getEntityLinks,
  addEntityLink,
  updateEntityLink,
  deleteEntityLink,
} from '../../../data/entityLinkData';

interface EntityLinksProps {
  sourceEntity: LinkSource;
  lockEdit: boolean;
}

// Ensure a user-entered url is an absolute link so the browser navigates away
// rather than treating it as a relative path.
const toHref = (url: string): string => {
  if (!url) return url;
  return /^https?:\/\//i.test(url) ? url : `https://${url}`;
};

const formatAudit = (date: string | null, name: string | null): string => {
  if (!date && !name) return '';
  const when = date ? new Date(date).toLocaleDateString('en-GB') : 'N/A';
  return `${when}${name ? ` by ${name}` : ''}`;
};

const EntityLinks = ({ sourceEntity, lockEdit }: EntityLinksProps) => {
  const [links, setLinks] = useState<EntityLink[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [newUrl, setNewUrl] = useState('');
  const [saving, setSaving] = useState(false);

  const [editingId, setEditingId] = useState<number | null>(null);
  const [editUrl, setEditUrl] = useState('');

  const fetchLinks = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getEntityLinks(sourceEntity);
      setLinks(data);
      setError(null);
    } catch (err) {
      console.error('Error fetching links:', err);
      setError('Failed to load links.');
    } finally {
      setLoading(false);
    }
  }, [sourceEntity]);

  useEffect(() => {
    fetchLinks();
  }, [fetchLinks]);

  const handleAdd = async () => {
    const url = newUrl.trim();
    if (!url) return;

    try {
      setSaving(true);
      await addEntityLink(sourceEntity, url);
      setNewUrl('');
      await fetchLinks();
    } catch (err) {
      console.error('Error adding link:', err);
      setError('Failed to add link.');
    } finally {
      setSaving(false);
    }
  };

  const handleStartEdit = (link: EntityLink) => {
    setEditingId(link.entityLinkId);
    setEditUrl(link.url);
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditUrl('');
  };

  const handleSaveEdit = async (entityLinkId: number) => {
    const url = editUrl.trim();
    if (!url) return;

    try {
      setSaving(true);
      await updateEntityLink(entityLinkId, url);
      handleCancelEdit();
      await fetchLinks();
    } catch (err) {
      console.error('Error updating link:', err);
      setError('Failed to update link.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (entityLinkId: number) => {
    try {
      await deleteEntityLink(entityLinkId);
      await fetchLinks();
    } catch (err) {
      console.error('Error deleting link:', err);
      setError('Failed to delete link.');
    }
  };

  const inputStyle: React.CSSProperties = {
    flex: 1,
    padding: '0.25rem 0.5rem',
    borderRadius: '0.25rem',
    border: '1px solid #ccc',
    fontFamily: 'inherit',
    fontSize: '0.95rem',
  };

  return (
    <div style={{ padding: '1rem' }}>
      {error && (
        <div style={{ color: '#d13438', marginBottom: '0.75rem', fontSize: '0.9rem' }}>{error}</div>
      )}

      {!lockEdit && (
        <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.25rem', alignItems: 'stretch' }}>
          <input
            type="text"
            value={newUrl}
            placeholder="Enter a URL (e.g. https://example.com)"
            onChange={(e) => setNewUrl(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleAdd();
            }}
            style={inputStyle}
            disabled={saving}
          />
          <button
            type="button"
            onClick={handleAdd}
            disabled={saving || !newUrl.trim()}
            className="form__edit__button accept"
            style={{ flex: '0 0 auto', width: 'auto', margin: 0, padding: '0 0.9rem', whiteSpace: 'nowrap' }}
          >
            Add Link
          </button>
        </div>
      )}

      {loading ? (
        <p style={{ color: '#666' }}>Loading links...</p>
      ) : links.length === 0 ? (
        <div
          style={{
            display: 'flex',
            padding: '1rem',
            backgroundColor: 'rgb(216 229 237)',
            borderRadius: '0.5rem',
            alignItems: 'center',
          }}
        >
          <p style={{ margin: 0 }}>No links added yet</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {links.map((link) => (
            <div
              key={link.entityLinkId}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: '10px',
                padding: '0.6rem 0.8rem',
                border: '1px solid #e1e1e1',
                borderRadius: '0.4rem',
                backgroundColor: '#fff',
              }}
            >
              {editingId === link.entityLinkId ? (
                <>
                  <input
                    type="text"
                    value={editUrl}
                    onChange={(e) => setEditUrl(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleSaveEdit(link.entityLinkId);
                      if (e.key === 'Escape') handleCancelEdit();
                    }}
                    style={inputStyle}
                    autoFocus
                    disabled={saving}
                  />
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      type="button"
                      onClick={() => handleSaveEdit(link.entityLinkId)}
                      disabled={saving || !editUrl.trim()}
                      title="Save"
                      style={{ cursor: 'pointer', border: 'none', background: 'none', color: '#107c10' }}
                    >
                      <FaCheck />
                    </button>
                    <button
                      type="button"
                      onClick={handleCancelEdit}
                      title="Cancel"
                      style={{ cursor: 'pointer', border: 'none', background: 'none', color: '#666' }}
                    >
                      <FaTimes />
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0, flex: 1 }}>
                    <a
                      href={toHref(link.url)}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        color: '#0078d4',
                        textDecoration: 'none',
                        wordBreak: 'break-all',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px',
                      }}
                    >
                      <FaExternalLinkAlt size={11} />
                      {link.url}
                    </a>
                    <span style={{ fontSize: '0.72rem', color: '#888', marginTop: '2px' }}>
                      {`Created: ${formatAudit(link.createdDate, link.createdByName) || 'N/A'}`}
                      {link.modifiedDate ? ` · Modified: ${formatAudit(link.modifiedDate, link.modifiedByName)}` : ''}
                    </span>
                  </div>
                  {!lockEdit && (
                    <div style={{ display: 'flex', gap: '10px' }}>
                      <button
                        type="button"
                        onClick={() => handleStartEdit(link)}
                        title="Edit"
                        style={{ cursor: 'pointer', border: 'none', background: 'none', color: '#0078d4' }}
                      >
                        <FaEdit />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDelete(link.entityLinkId)}
                        title="Delete"
                        style={{ cursor: 'pointer', border: 'none', background: 'none', color: '#d13438' }}
                      >
                        <FaTrash />
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default EntityLinks;
